/**
 * `WorkflowSchemaCatalog.ts` — the AGENT-FACING schema for `workflow.yaml`,
 * versioned, served by the `get_workflow_schema` tool.
 *
 * Its counterpart for PEOPLE is `WORKFLOW_FILE_SPEC_documentation.md` beside it,
 * which the agent never sees: storage layout, the codec, the component map, and
 * the reasoning. Same format, different audiences — a format change usually has
 * to land in both.
 *
 * WHY THIS EXISTS. The document's shape was only ever taught to the model
 * through prompt text (the workflow-builder role) and one tool
 * description, both of which are injected on every turn and therefore have to
 * stay short. That left the model writing patches against a summary: it knew
 * `scripts` replaces a list, and it could not check a key it half-remembered
 * without guessing and reading the refusal. This module is the long form,
 * pulled ON DEMAND — the model asks for the schema when it is about to author
 * or verify a document, and pays for it only then.
 *
 * IT IS A SPECIFICATION, NOT INSTRUCTIONS (user direction, 2026-08-20). It
 * describes the FORMAT — structure, keys, types, defaults, patch semantics and
 * the command reference — and reads as something any reader could use: a
 * person, another tool, an agent. It carries no second-person guidance, no
 * "rules you must not break", no "how to use this", and it names no internal
 * tool: the agent receiving it is a client-surface agent, so a lookup it cannot
 * invoke says nothing about the format (`neutralizeToolReferences` strips those
 * out of the registry's own argument descriptions). The VALIDATION report moved
 * out for the same reason — which half of it blocks Play, and what to do about
 * each, is how to work rather than what the format is. All of that belongs to
 * `public/AI/roles/workflow-builder.md`, the one surface that knows how this
 * agent should work.
 *
 * The test when adding text here: could a reader who is not our agent — someone
 * writing a `workflow.yaml` by hand, or a tool generating one — use this
 * sentence? If it only makes sense as an order to our agent, it belongs in the
 * role.
 *
 * WHY IT LIVES BESIDE THE CODEC, not in `public/AI/` with the roles and
 * skills. Most of what a schema has to be exact about is already declared in
 * code: the enums in `WorkflowDocument.ts`, the accepted key sets in
 * `WorkflowYaml.ts`, and the tool parameter schemas in
 * `@ai/registry/scriptCommandCatalog`. Rendering off those constants means
 * adding a key, an enum value or a command updates the published schema in the
 * same edit, instead of leaving a surface to drift (see the change checklist in
 * `Campfire_Web_Workflow.md`). What CANNOT be derived — the semantics and the
 * patch behaviour — is written out below, and that prose is a checklist
 * surface like any other.
 *
 * VERSIONING. A schema version IS the format version the file's `version` key
 * names ({@link WORKFLOW_FORMAT_VERSION}). Only the CURRENT entry may render
 * from live constants; when the format version bumps, freeze the outgoing
 * entry to a literal snapshot of what it served, or it will silently start
 * describing the new format under the old number. Note the client parses only
 * the current format (no backward compatibility, by direction — see the file
 * spec's compatibility block), so an older schema is reference for reading a file
 * that predates this build, never a promise that it will load.
 *
 * Pure logic — no DOM, no Vue, no IOC.
 */

import {
  COMPLETION_CONDITIONS,
  MAX_COMPLETION_DELAY_SECONDS,
  MEDIA_FITS,
  MEDIA_TYPES,
  SAY_STEP_INSTRUCTIONS_TOKEN,
  SCRIPT_REVERTS,
  WORKFLOW_PANEL_DOCK_ALIGNS,
  SCRIPT_TRIGGERS,
  STEP_TRIGGER_TYPES,
  WORKFLOW_FORMAT_VERSION,
  WORKFLOW_TYPES,
  USER_INPUT_ALLOWANCES,
} from "./WorkflowDocument";
import { USER_NAME_TOKEN } from "./workflowTextTokens";
import {
  WORKFLOW_COMPLETION_CONDITION_KEYS,
  WORKFLOW_DOCUMENT_KEYS,
  WORKFLOW_HEADER_KEYS,
  WORKFLOW_IDENTITY_KEYS,
  WORKFLOW_UI_KEY,
  WORKFLOW_MEDIA_KEYS,
  WORKFLOW_STEP_VIEW_KEYS,
  WORKFLOW_PATCH_HEADER_KEYS,
  WORKFLOW_PATCH_KEYS,
  WORKFLOW_PATCH_STEP_KEYS,
  WORKFLOW_RUNTIME_KEY,
  WORKFLOW_SCRIPT_KEYS,
  WORKFLOW_SCRIPT_STEP_KEYS,
  WORKFLOW_STEP_KEYS,
  WORKFLOW_TRIGGER_KEYS,
} from "./WorkflowYaml";
import {
  isStructuralScriptCommand,
  listScriptCommands,
  type ScriptCommandArg,
  type ScriptCommandSpec,
} from "@ai/registry/scriptCommandCatalog";

/** One published schema version. */
export interface WorkflowSchemaVersion {
  /** Format version this schema describes, e.g. `0.2`. */
  readonly version: string;
  /** The schema text (Markdown, written for a model to read). */
  readonly text: string;
  /** True when this is the version this build reads and writes. */
  readonly isCurrent: boolean;
  /** True when debug mode is serving developer-uploaded text for this version. */
  readonly isPublished: boolean;
}

/** The format version this build reads and writes. */
export const CURRENT_WORKFLOW_SCHEMA_VERSION = WORKFLOW_FORMAT_VERSION;

/**
 * Stable download/upload name for the current schema. It includes the format
 * version deliberately: when the codec advances, an override authored against
 * the old format must not silently describe the new one.
 */
export const WORKFLOW_SCHEMA_OVERRIDE_FILE_NAME =
  `workflow-schema-v${WORKFLOW_FORMAT_VERSION}.md`;

export interface WorkflowSchemaResolveOptions {
  /** Debug-only replacement for the CURRENT version; older versions stay frozen. */
  readonly currentTextOverride?: string | null;
}

/**
 * Aliases a caller may pass instead of a number to mean "whatever is current".
 * `get_workflow_schema` defaults to this when the parameter is omitted.
 *
 * Exported because the debug panel's Workflow tab lists what a caller may pass
 * for each published version, and re-typing them there would be a second place
 * this set is written down — the thing this whole module is arranged to avoid.
 */
export const WORKFLOW_SCHEMA_LATEST_ALIASES: ReadonlyArray<string> = [
  "latest",
  "current",
  "default",
];

const K_LATEST_ALIASES: ReadonlySet<string> = new Set(WORKFLOW_SCHEMA_LATEST_ALIASES);

/**
 * Published versions, newest first. One entry per format version that has ever
 * been served; only the head may be generated (see the module header).
 */
const K_SCHEMA_BUILDERS: ReadonlyArray<{ version: string; build: () => string }> = [
  { version: WORKFLOW_FORMAT_VERSION, build: buildSchemaV0_2 },
];

/** Every published version, newest first. */
export function listWorkflowSchemaVersions(): ReadonlyArray<string> {
  return K_SCHEMA_BUILDERS.map((entry) => entry.version);
}

/**
 * Resolves a schema by version. An empty / omitted argument and the "latest"
 * aliases resolve to {@link CURRENT_WORKFLOW_SCHEMA_VERSION}; a leading `v` is
 * tolerated (`v0.2`). Returns null when the version is unknown, so the caller
 * can name the ones that exist rather than serving a wrong document.
 */
export function getWorkflowSchema(
  version?: string | null,
  options?: WorkflowSchemaResolveOptions,
): WorkflowSchemaVersion | null {
  const requested = (version ?? "").trim().toLowerCase().replace(/^v/, "");
  const wanted =
    requested.length === 0 || K_LATEST_ALIASES.has(requested)
      ? CURRENT_WORKFLOW_SCHEMA_VERSION.toLowerCase()
      : requested;

  const entry = K_SCHEMA_BUILDERS.find((candidate) => candidate.version.toLowerCase() === wanted);
  if (entry === undefined) return null;

  const isCurrent = entry.version === CURRENT_WORKFLOW_SCHEMA_VERSION;
  const currentOverride = options?.currentTextOverride;
  const isPublished = isCurrent && !!currentOverride?.trim();

  return {
    version: entry.version,
    text: isPublished ? currentOverride! : entry.build(),
    isCurrent,
    isPublished,
  };
}

// ---------------------------------------------------------------------------
// v0.2 — the current format
// ---------------------------------------------------------------------------

function list(values: ReadonlyArray<string>): string {
  return values.map((value) => `\`${value}\``).join(", ");
}

// ---------------------------------------------------------------------------
// The script-command reference (generated)
// ---------------------------------------------------------------------------

/**
 * Renders one argument as a single reference line.
 *
 * Compact on purpose: the reference lists ~45 commands, and a multi-line entry
 * per argument would put the schema past what is worth pulling. The parts a
 * wrong value actually turns on — the type, the enum, and whether it is
 * required — are all here; the prose is the registry's own.
 */
/**
 * Rewrites a registry description for a reader who is NOT holding the tool.
 *
 * The registry's text is written for a live caller — "Hierarchy paths from
 * Get_Scene_Context" is exactly right when you can call it. This document is a
 * format specification: its reader may be a person writing YAML by hand or a
 * tool generating it, and naming an internal lookup they cannot invoke tells
 * them nothing about the format. The FACT survives (these are hierarchy paths);
 * only the instruction to go and fetch one is dropped.
 *
 * A phrasing this does not know is left verbatim rather than mangled — the
 * worst case is one stale sentence, not a wrong one.
 */
function neutralizeToolReferences(description: string): string {
  return (
    description
      .replace(/\bfrom Get_Scene_Context\b/gi, "from the scene hierarchy")
      .replace(/\bUse Get_Project_Context to see\b/gi, "See")
      .replace(/\bcall Get_Scene_Context to\b/gi, "identify")
      // The "resolve targets first" preamble takes several shapes and each has
      // its own continuation, so each is rewritten whole rather than deleted —
      // deleting leaves ". and pass at least one of …" behind.
      .replace(/\bResolve (?:part )?targets first \(see system prompt\)\s*(?:and|-|,)\s*/gi, "")
      .replace(/\bResolve (?:part )?targets first \(see system prompt\)\.\s*/gi, "")
      .replace(/\bResolve (?:part )?targets first \(see system prompt\)\s*/gi, "")
      // Whatever the rewrite left, a sentence still starts with a capital.
      .replace(/(^|[.!?]\s+)([a-z])/g, (_m, lead: string, letter: string) => lead + letter.toUpperCase())
      .replace(/\s{2,}/g, " ")
      .trim()
  );
}

function renderArg(arg: ScriptCommandArg): string {
  const type = arg.type === "array" ? `${arg.itemType || "string"}[]` : arg.type || "any";
  const flag = arg.required ? " **required**" : "";
  const values = arg.enumValues.length > 0 ? ` one of: ${arg.enumValues.join(" | ")}.` : "";
  const description = neutralizeToolReferences(arg.description);
  return `  - \`${arg.name}\` (${type})${flag} —${values} ${description}`.trimEnd();
}

function renderCommand(command: ScriptCommandSpec): string {
  const args =
    command.properties.size === 0
      ? "  - _(no arguments)_"
      : [...command.properties.values()].map(renderArg).join("\n");
  // The structural note rides the command it applies to rather than a list
  // elsewhere: it is a property of THAT command, and a reader choosing between
  // commands should not have to go and look it up. Stated as a fact about what
  // the command does, like everything else in this reference.
  const structural = isStructuralScriptCommand(command.name)
    ? "\n\n_Edits the project itself; no `revert` mode restores it._"
    : "";
  return `### \`${command.name}\`\n\n${neutralizeToolReferences(command.description)}${structural}\n\n${args}`;
}

/**
 * The list of commands a script step may name, with the exact arguments each
 * takes — GENERATED from the live tool registry.
 *
 * This section exists because the Workflow Builder is the one role that writes
 * tool calls without holding the tools: it authors them into `workflow.yaml`
 * and must never fire one at the author's live scene. Every other role learns a
 * tool's arguments from the tool's own schema in `session.update`; this role has
 * to be told. Generating it from `AIAgentToolRegistryModel`'s registrations —
 * the same records the executor dispatches on — is what stops the documentation
 * and the tools becoming two sources of truth.
 *
 * The authoring rules that open the section are ported from Unity's
 * `ScriptClassifier` system prompt, which solved this same problem for recorded
 * Actions. The load-bearing one is REPLAY SAFETY: a recorded call is not
 * applied when it is written, so it cannot inherit the state an earlier step
 * left behind.
 */
function buildScriptCommandReference(): string {
  const commands = listScriptCommands();

  // No category grouping: the list is short and deliberately curated, and it
  // is already ordered the way a lesson reads (show/hide → emphasis → motion →
  // waits). Bucketing nine entries under four headings hid that order.
  const sections = `## The commands\n\n${commands.map(renderCommand).join("\n\n")}`;


  return `# Script command reference

The commands a script step may name, and the arguments each takes. A step is
\`{ tool: <id>, args: { … } }\`, where \`args\` is a YAML mapping whose keys and
values are exactly those of the corresponding live tool call — same names, same
types, same enum values.

An \`args\` key that is not a parameter of the named command is invalid and
refuses the document. Everything else is reported against the step rather than
refusing it, because it is recoverable and because the executors are more
tolerant than the declarations — enum values are matched after folding case and
separators, and some accept documented synonyms. Reported that way: a \`tool\`
id that is not a Campfire tool at all, one that this client does not support
(the step would fail with \`tool_not_supported\`), a command that cannot run as
a step, an argument of the wrong type, a value outside a declared enum, and a
missing required argument.

## Behaviour at playback

- A command's target is an IDENTIFIER, not a description: a part is addressed by
  its hierarchy path (\`0,1,2\`), a scene by its number, a selection set or a
  card by its exact name. A display name a person would say is not a valid
  argument value.
- A script step is executed when the run reaches it, not when it is written, and
  each step is dispatched independently. State an earlier step produced — a
  selection, an isolation, a camera position — is not carried into a later
  step's arguments; each step names its own targets.
- \`isolate\`, \`highlight_part\` and \`hide_parts\` persist beyond the step that
  applies them until a clearing command runs (\`clear_isolation\`,
  \`clear_highlight\`, \`unhide_parts\`) or the script's \`revert\` mode restores the
  scene.
- A step whose command fails does not stop the script: the failure is logged and
  the next step runs.

## Example

Here \`0,1,2\` is a hierarchy path resolved beforehand, repeated in each step that
targets it:

\`\`\`yaml
scripts:
  - id: show-the-fan
    name: Show the fan
    trigger: on_start
    revert: on_action_finish     # puts the scene back when the learner moves on
    steps:
      - say: Let's look at the fan assembly.
      - tool: isolate
        args:
          partPathsToIsolate: ["0,1,2"]
      - tool: highlight_part
        args:
          partPathsToHighlight: ["0,1,2"]
          semanticColor: attention
          pulseCount: 2
      - say: Can you see the two clips at the rear?
      - tool: wait_for
        args:
          trigger: user_response
          userResponseCondition: confirms they found the clips
      - tool: Toggle_Model_Animation
        args:
          enabled: true
          partPath: "0,1,2"
          take: Release
          speedPercent: 50
      - tool: clear_highlight
        args:
          partPathsToClearHighlight: ["0,1,2"]
\`\`\`

The animation step names a take and a speed: every argument of that command is
optional and each keeps its current value when omitted, so a later step can
change the speed alone. A take is named by the label the scene lookup reports
for that model, and the animation keeps playing with those settings until
something changes them — a script that starts one should stop it, or set
\`playback: once\`.

Reading that example against the behaviour above: the \`wait_for\` follows the \`say\`
that asks the question, so the pause begins after the learner has been asked;
\`revert: on_action_finish\` restores the isolation when the script ends, while
\`clear_highlight\` removes the highlight explicitly; and each step carries the
path rather than inheriting it. Command ids are case-sensitive as written —
\`Toggle_Model_Animation\` is mixed-case, \`isolate\` is lower.

${sections}
`;
}

function buildSchemaV0_2(): string {
  return `# Campfire workflow schema — format version ${WORKFLOW_FORMAT_VERSION}

The per-project guided-training definition: ONE YAML file per project
(\`workflow.yaml\`) holding the authored steps a learner walks through and the
prepared scripts that play on them. Mutable run progress is stored per user
outside this file and is not part of it.

This document describes the FORMAT only — its structure, its keys, their types
and defaults, how a patch is applied, and what makes a file valid.

## Versioning

This document describes format ${WORKFLOW_FORMAT_VERSION}. A stored file names its own format in its
\`version\` key, and the validation report flags a file that names a different
one. Other published versions are retrievable through \`get_workflow_schema\`'s
\`version\` parameter. This client reads and writes ${WORKFLOW_FORMAT_VERSION} only: an older schema
explains a file that predates this build, it does not make it loadable.

## The three surfaces (same document, three views)

| Surface | What it is | Who edits it |
| --- | --- | --- |
| The stored file | The canonical YAML below | The app (via the two paths beside it) |
| The authored view | An MD-like rendering shown in the Workflow editor | The human author, live |
| The patch | A sparse YAML document using this same schema | An agent, via \`update_workflow\` |

All three write the same underlying model, so a patch appears in the human's
editor immediately, and their unsaved keystrokes are flushed into the document
before a patch reads it. A patch is sparse by design — only the keys it carries
are applied. The one case where a whole document is sent instead is repairing a
file that failed to parse: there is no parsed model to patch, so the complete
corrected file replaces the stored bytes.

## The full shape

\`\`\`yaml
# ---- file metadata: app-owned. Present in a stored file; not patchable. ----
uid: 8f3c1a2e-...           # stable workflow id
type: ${WORKFLOW_TYPES[0]}           # workflow kind — strict enum: ${list(WORKFLOW_TYPES)}
version: "${WORKFLOW_FORMAT_VERSION}"              # the format this file was written against
project:
  id: 4c9f...
  name: Robodog

# ---- the header ----
workflow:
  name: Robodog Familiarization      # REQUIRED — a missing name is a validation error
  description: General familiarity with the Robodog
  takes_about: 5 min
  language: en                       # the language the guide speaks
  product: Robodog                   # what the workflow is about
  user_input_allowed: [ai_input]     # what the learner may reach; ${list(USER_INPUT_ALLOWANCES)}; absent = nothing
  autostart_desktop: false           # play by itself when the project opens on desktop? absent = false
  autostart_headset: true            # the same for a headset session
  remember_progress: false           # does a new run resume where the last stopped? absent = false
  requires_console_placement: true   # a headset run seats the stage in the room first? absent = false
  lock_ui: false                     # freeze the run's panels for everyone, author included? absent = false
  use_custom_ui: false               # does this workflow define its own panel arrangement? absent = false
  disable_mobile_keyboard: false     # on a phone, the chat field talks only and never raises the keyboard? absent = false
  knowledge:                         # documents it draws on — MACHINE-OWNED, no patch path
    include:
      - doc: torque-spec-table

# ---- optional grouping. Membership lives on each step's \`group\` key; this
#      list carries ordering and empty groups. ----
groups:
  - name: Getting started

# ---- the steps. This list IS the play order. ----
steps:
  - id: overview                     # optional stable id — REQUIRED to rename safely
    name: Overview                   # steps are addressed by name unless they carry an id
    disabled: false                  # true parks the step: kept in the file, never played; absent = false
    group: Getting started           # membership; a group's steps must be CONTIGUOUS
    scene: Scene 1                   # name, 1-based index, or id; empty = "plays wherever the learner is"
    takes_about: 1 min
    step_context: Covers the fan housing and its two clips.  # INTERNAL: how you recognise this step
    step_instructions: |-            # what the LEARNER reads, as MARKDOWN; empty shows none
      Undo the two clips, then lift the housing **straight** off.

      - Loosen the LEFT clip first
      - Then the right
    back_button: ""                  # the button's OPTIONAL label; empty = the arrow with no words
    next_button: Next                # here the label IS the switch; pressing Next completes the step and advances
    is_completion_step: true         # at least one enabled step must set this; true = Next ENDS the run here
    completion_condition:            # optional — hold Next back until something happens
      type: delay                    # ${list(COMPLETION_CONDITIONS)}
      delay_seconds: 5               # \`delay\` only
    user_input_allowed: [all]        # per-step override of the header's list
    attachment:                      # optional — what this step points at
      part: Robodog/Fan              # slash-joined DISPLAY-NAME path (not the 0,1,2 tool path)
      point: { x: 0.1, y: 0.2, z: 0.3 }   # optional exact world point on it
    step_view:                       # optional — the camera flies here when the step starts
      position: { x: 1.42, y: 0.98, z: -2.15 }   # the camera, world metres
      pivot: { x: 0, y: 0.4, z: 0 }              # the point it looks at and orbits
    media:                           # optional — an image or video beside the step
      type: video                    # ${list(MEDIA_TYPES)}; omit to read it from the url
      fit: contain                   # ${list(MEDIA_FITS)}; omit for fill — never crop this one
      url: campfire://media/fan-teardown-1a2b3c4d.mp4   # or any https:// address
      loop: true                     # video only; omit to play it once
      autoplay: true                 # video only; starts on arrival instead of a play_video script command
      name: Fan teardown
      description: The order the blades come off in.
      transcript: |-                 # video only — what is said, so it can be searched
        First release the two clips at the rear.

        The housing lifts straight off once both are clear.
      start: "0:12"                  # video only — seconds, mm:ss, or hh:mm:ss
      end: "1:45"
    triggers:                        # branch buttons the play panel shows
      - type: ${STEP_TRIGGER_TYPES[0]}                 # ${list(STEP_TRIGGER_TYPES)} — the only type so far
        label: Explore the fan
        go_to: Fan Assembly          # a step or group name (a group lands on its first step)
    scripts:                         # YOUR prepared, executable scripts
      - id: overview-intro           # required, unique within the step
        name: Overview Intro
        trigger: on_start            # ${list(SCRIPT_TRIGGERS)}
        revert: on_scene_change      # ${list(SCRIPT_REVERTS)} (default \`none\`)
        steps:
          - say: Welcome — let's look at the fan
          - say: <step_instructions>    # speaks this step's step_instructions
          - tool: isolate
            args:
              partPathsToIsolate: [Robodog/Fan]
\`\`\`

## Field reference

Accepted top-level keys: ${list(WORKFLOW_DOCUMENT_KEYS)}.
Anything else is an error naming the key.

**Ownership:**

| Tier | Keys | Writable |
| --- | --- | --- |
| Identity / file metadata | ${list(WORKFLOW_IDENTITY_KEYS)} | read only — patching any of these is REFUSED |
| Retired shared state | \`${WORKFLOW_RUNTIME_KEY}\` | REFUSED; per-user progress lives outside this file in DynamoDB |
| Authored | the header + each step's authored fields | edit only what a fix requires — this is the human's text |
| Machine | each step's \`scripts\` and \`triggers\`, \`workflow.knowledge\` | \`scripts\` / \`triggers\` are YOURS to write; \`knowledge\` has no patch path |
| Presentation | \`${WORKFLOW_UI_KEY}\` | written by the app when a person moves the run's panels; it has no patch path |

**\`${WORKFLOW_UI_KEY}\`** — optional, and written by the application rather
than by hand. It records where a run's floating panels were placed when someone
moved them away from their default arrangement, so the next run can restore it.
**Only read when \`use_custom_ui\` is true**: without that flag the panels are
arranged by whoever is looking at them and the arrangement is remembered in
their own browser, so this section is ignored entirely and nothing anybody drags
is written here.
It holds one key, \`panels_layout\`: a list of \`{ panel, dock?, screen?,
position?, embed? }\` entries, one per panel that deviates. A panel sitting in
its default place is absent from the list, and an empty list means the section
itself is absent. A \`dock\` names the face it sits on and, optionally, where
along that face (\`align\` ∈ ${list(WORKFLOW_PANEL_DOCK_ALIGNS)}). A
\`position\` is the panel's top-left in viewport pixels.

**\`workflow\` (the header)** — accepted keys: ${list(WORKFLOW_HEADER_KEYS)}.

- \`name\` — REQUIRED. A blank name is a validation error.
- \`description\` / \`takes_about\` — free text. A missing description warns.
- \`language\` — the language the guide speaks (\`en\`, \`es\`, ...).
- \`product\` — the product or equipment the workflow covers. Free text.
  Stored as typed; Dynamo and vector lookups trim, collapse whitespace, and
  lowercase it so \` RoboDog\` matches a catalog key of \`robodog\`. Do not
  rewrite the author's spelling.
- \`user_input_allowed\` — a LIST of what the learner may reach; members are
  ${list(USER_INPUT_ALLOWANCES)}. **Absent or empty means nothing**, so a run
  hides both surfaces and the learner can only follow the steps. The two
  capabilities are independent:
    • \`ai_input\` — Spark's composer. They can ask questions; the guide answers
      without losing their place. The composer appears WITHOUT any way to reach
      the tools.
    • \`tools\`    — the scene tool palette (select, measure, slice, ...).
  \`all\` is a third member meaning every capability, and it keeps meaning that:
  a list carrying it also opens whatever capability a later version adds, where
  \`[ai_input, tools]\` names exactly those two forever. \`[all]\` and
  \`[ai_input, tools]\` are therefore identical today and may differ later.
  A guided procedure is the constrained case; a workflow that wants an open
  session says so. It must be a list — a bare \`user_input_allowed: ai_input\` is
  a validation error, not a one-item list. A member this client does not know is
  a warning, not an error: it opens nothing and the rest of the list still holds.
- \`autostart_desktop\` / \`autostart_headset\` — booleans, **both absent = false**.
  When true, opening the project starts the run automatically on that kind of
  client (headset = a WebXR session, or a browser that runs on a headset, where
  entering a project already enters XR; desktop = everything else). They are
  separate flags on purpose: opening a project on a desktop is usually an act of
  EDITING, while entering one on a headset is already an act of viewing.
  Autostart only fires on a workflow that can actually play — the same
  condition Preview uses: at least one live step, at least one live
  \`is_completion_step\`, and NO validation errors. There is no separate ready
  flag; setting a flag on a workflow that still has errors does nothing until
  those errors are gone. Must be real booleans.
  An autostarted run does not begin the instant the project is opened: it waits
  for the project and its scene to finish loading, then a couple of seconds
  more, so the first step is not played underneath the loading screen.
  These flags decide what happens for someone who can EDIT the project. A
  viewer — anyone whose role on the project is not owner or editor — always
  gets the run started for them, whatever the flags say, because for them the
  project is the workflow and there is nothing else to open it for; and when
  the run finishes they are returned to their projects list rather than left in
  the editor. So \`autostart_desktop: false\` means "do not interrupt whoever is
  building this", never "nobody is ever taken straight into it".
- \`remember_progress\` — boolean; **absent means false**. It decides what a NEW
  run starts from. Absent/false: every Play starts the learner on the first step
  with nothing completed and nothing seen, which is what a training workflow
  normally wants and what makes a preview repeatable. True: the run resumes on
  the step the last one stopped on and inherits its completed + \`visited\` lists
  and the \`visit_stack\` path,
  so the guide may take the learner back over ground covered in an earlier
  session. It gates only the READ of the current user's DynamoDB state — a run
  always publishes where it is, either way.
- \`requires_console_placement\` — boolean; **absent means false**. When true, a
  run on a HEADSET starts by asking the wearer where the stage goes: console
  placement opens, they aim the table around their real room, and the workflow
  begins once they are done. Written for a procedure that has to line up with
  something physical, where being asked where is part of starting it.
  There is no desktop counterpart and no second flag for one — console placement
  is an XR mode with nothing on a desktop to move — so a desktop run ignores it
  and starts straight away, the way \`autostart_headset\` is ignored on a desktop.
  It is asked on every start, so a Stop and another Play asks again; the whole
  gesture is one pinch at the floor, and cancelling starts the run too (it means
  "keep the stage where it is"). A client that cannot place — no live session —
  simply starts. Must be a real boolean.
- \`lock_ui\` — boolean; **absent means false**. True freezes the run's floating
  panels — the play panel, the run toolbar, the Back/Next row, Spark's composer,
  the step-media band and the outline — where the workflow puts them: they
  cannot be dragged, docked, undocked or reset by anyone.
  It applies to EVERY user, including whoever can edit the project. That is what
  separates it from the ordinary rule that a learner cannot rearrange the panels
  while an author can: this is the author's own statement that the arrangement
  is part of the workflow, so exempting them from it would make it a preference
  rather than a lock. An author who wants to move a panel turns it off first.
  It freezes an arrangement rather than describing one. Where the panels sit is
  the workflow's saved layout, or the default layout when it has none, so the
  order is arrange, then lock; locking a workflow nobody has arranged simply
  pins the default. It only has anything to freeze while a run is playing —
  outside one there are no floating panels — so it changes nothing about
  authoring except that the author's own preview is locked too.
  Must be a real boolean.
- \`knowledge.include\` — a list of \`{ doc: <key> }\` entries naming canonical
  document keys (\`!document:<key>\` in the document catalog). Authored values
  are stored as typed; lookups use the same trim / whitespace-collapse /
  lowercase grammar as \`product\`. Each included document is validated, synced
  into the customer vector store with this workflow's id + the normalized
  \`doc\` key as metadata, and searched only for this workflow. Product
  documents and directly included documents are searched together, but their
  vector scopes stay separate. Removing a doc from this list removes this
  workflow-scoped vector attachment; adding or changing one syncs it before
  search. There is NO patch path: sending \`workflow.knowledge\` is refused.
- \`use_custom_ui\` — boolean; **absent means false**. It decides WHOSE the run's
  panel arrangement is.
  Absent or false — the normal case — leaves it to whoever is looking at the
  run: they can drag the panels around and snap them to each other or to the
  screen, and where they put them is remembered in THEIR browser and reused for
  every other workflow that also leaves it to them. Nothing they do reaches this
  file, and nesting a panel inside another is not offered, because an
  arrangement made in passing is a set of positions rather than a composition.
  True hands the arrangement to the workflow: it is saved here in
  \`${WORKFLOW_UI_KEY}\`, only somebody who can edit the project may change it,
  every learner is given the author's layout and cannot move a panel at all, and
  panels may be nested inside one another.
  ⚠ Set it FIRST, then arrange. The two stores never mix, so a layout dragged
  around while it was off stays in that browser and is NOT adopted when it is
  turned on — turning it on starts from the default arrangement.
  It is also what makes \`lock_ui\` mean something: locking without it freezes
  whatever arrangement each viewer's own browser happens to hold, which is
  almost certainly not what the author meant, and validation warns about it.
- \`disable_mobile_keyboard\` — boolean; **absent means false**. True makes the
  learner's chat field a talk-only control on a PHONE for the whole run: the
  on-screen keyboard never comes up.
  Absent or false, a tap on the field starts listening and a second tap on it
  stops listening AND opens the keyboard, so the learner can type instead. True
  keeps the first half and drops the second: the field is a listen/stop toggle
  and nothing raises a keyboard. A hardware keyboard still types.
  It does not decide whether the chat is THERE — that is each step's
  \`user_input_allowed\`, and withholding \`ai_input\` is how a step removes the
  field entirely.
  It reaches a phone only. A desktop has no on-screen keyboard to raise, so a
  run there is unaffected and a desktop preview shows no difference.
  Must be a real boolean.
- \`knowledge.include\` — a list of \`{ doc: <id> }\` entries. Carried and
  shape-checked, but nothing resolves the ids yet, and there is NO patch path:
  sending \`workflow.knowledge\` is refused.

**\`groups\`** — a name, or \`{ name }\`. A group exists to carry ordering and an EMPTY
group. Membership itself lives on each step's \`group\` key, and the
app rebuilds this list after every edit. Group names are addressable (a
trigger's \`go_to\`, the play panel's "n of m" scope), so duplicates are invalid
and a group named the same as a step is discouraged — the step wins the lookup.
**A group's steps must be CONTIGUOUS in step order**: membership is positional,
so a group interrupted by a step belonging elsewhere is not a valid document.

**\`steps[]\`** — accepted keys: ${list(WORKFLOW_STEP_KEYS)}.

- \`name\` — how a step is addressed by handoffs and trigger targets. Renaming a
  step therefore breaks every reference to it unless it carries an \`id\`.
- \`id\` — optional stable id. **Send \`id\` to rename a step**: with an \`id\` the
  patch resolves the existing step and rewrites trigger \`go_to\` targets that
  named the old name. Without one, a changed \`name\` matches nothing and the
  patch creates a SECOND step.
- \`group\` — see "Patch semantics" for what setting it moves.
- \`scene\` — a real scene: **name** (case-insensitive), **1-based index** (same
  numbering as the scenes list / \`go_to_scene\`), or scene **id**. Empty means
  the step plays wherever the learner already is. Unquoted \`scene: 1\` is an
  index.
  **This field BINDS the step to a scene at playback**: when the step becomes
  current the run activates this scene and waits for it before running the
  step's \`on_start\` script, so the script's part paths resolve against it. A
  \`go_to_scene\` script step targeting the step's own scene is therefore
  redundant. A reference that resolves to nothing is a warning, not a refusal:
  the step still plays, wherever the learner is.
  **Leaving it empty ALSO makes the step media-only**: a step with no scene and
  a \`media\` block is about that media and nothing else, so playback shows the
  media large and centred on screen instead of docked beside the step panel.
  Give a step a scene when the learner is meant to look at the 3D content and
  the media is reference material beside it; leave it empty when the media IS
  the step.
- \`disabled\` — boolean; **absent means false**. True PARKS the step: it stays
  in the file, keeps everything written on it, and is still edited and checked,
  but no run ever reaches it. It is skipped by the play order (Next steps over
  it), it is not counted in the "n of m" position or the step total, it cannot
  be jumped to, it is left out of the outline the learner sees, and any trigger
  button pointing at it is hidden rather than shown as a button that does
  nothing — validation warns about that button so it can be re-pointed.
  Use it to keep a step that is not ready to ship, or to take a broken one out
  of the way, without losing the writing. Deleting the step is what removes it;
  this is the reversible version, and the one to reach for when the author says
  "not yet" rather than "no".
  Disabling every step leaves nothing to play and is a validation ERROR, so at
  least one step must stay live.
- \`step_context\` — a one-line summary of what the step is about, **for the
  guide only**. It is never rendered to the learner. Its job is recognition:
  when the learner asks for a step by DESCRIBING it ("the bit about the
  bracket") rather than naming it, this is the text that identifies which step
  they mean, which the step's name alone often cannot. Keep it short and
  specific to the step. (It is \`description\` on the HEADER, one level up; on a
  step the two halves of the pair are named together — \`step_context\` for the
  guide, \`step_instructions\` for the learner.)
- \`step_instructions\` — the copy the play panel shows under the step name: what
  the learner is asked to do here. Empty or absent shows none: emptiness IS the
  hidden state, so there is no shown/hidden switch.
  This and \`step_context\` are deliberately separate fields and must not be
  written as copies of each other — one is a summary you match against, the
  other is the learner's own reading. A script's \`say\` can speak these
  instructions instead of repeating them (see \`steps[].scripts[].steps\`).

  **It is MARKDOWN**, and the panel renders it: blank-line separated paragraphs,
  \`- \` bullet lists, \`1. \` numbered lists, \`**bold**\`, \`*italic*\`,
  \`\` \`code\` \`\` and \`[links](https://example.com)\`. Write a multi-step
  instruction as a list rather than as one sentence with semicolons, and bold the
  part or control the learner has to find. The markup is for the PANEL: a \`say\`
  that speaks these instructions reads the words with the markers taken off, so
  emphasis is worth adding and nothing the learner needs may live only in it — and
  a link is heard as its label, never as its address. In YAML a multi-line value is
  a block scalar:

  \`\`\`yaml
  step_instructions: |-
    Undo the two clips, then lift the housing **straight** off.

    - Loosen the LEFT clip first
    - Then the right

    The housing is not fixed — do not force it.
  \`\`\`

  Two things it may NOT contain, because the authoring editor projects this
  field as a labelled block of prose and both are read as structure there:
  a \`#\` HEADING line (the step's name is already the panel's heading, so
  instructions have nothing to head), and a line that begins with one of this
  view's own field labels followed by a colon — \`Context:\`, \`Scene:\`,
  \`Back button:\`, \`Next button:\`, \`Attachment point:\`, \`Media …:\`,
  \`Commands:\`, \`Triggers:\`, or the header's \`Description:\`. An ordinary \`Note: …\` or \`Warning: …\` is fine —
  only that closed list is reserved. A value containing either still plays and
  still round-trips, but it disappears from the WYSIWYG editor, so the author
  can only change it in raw YAML or through you.

  **\`media.transcript\` is the other prose block and follows exactly this
  rule** — same two reserved constructs, same consequence. A transcript is
  speech, so it is written with names in front of it more often than
  instructions are: \`Alex: and then we release the clips\` is ordinary prose and
  is fine, because only the closed list above is structure. \`Name:\` is on that
  list, so a transcript that labels a speaker that way is the one shape to
  avoid.
- \`next_button\` — the Next button's LABEL, and here the label IS the switch:
  written shows the button with that wording, empty or absent hides it. There is
  no \`hidden\` keyword and no default label, so \`next_button: hidden\` is simply a
  button captioned "hidden", and a step with no \`next_button\` has no Next button
  at all. Hiding it is a real authoring act — it forces the learner to pick a
  trigger, which is how a branch point is written. Next is pure navigation: it
  completes the step and advances immediately.
- \`back_button\` — the Back button's OPTIONAL label. It does NOT follow
  \`next_button\`'s rule and cannot hide anything: the Back button is shown
  whenever the learner has an earlier step on the path they walked, and this key
  only decides whether it carries words. Empty or absent draws it as the arrow
  alone, which is the common case. Writing \`hidden\` here produces a Back button
  captioned "hidden".
  Whether the learner can go back is a fact about their path, which changes as
  they move and which no author can know in advance — so it is not something a
  step gets to decide, and there is no way to write a step that traps them.
- \`is_completion_step\` — boolean; **absent means false**. True marks this step
  as the workflow's ENDING: pressing Next completes it and leaves playback
  instead of advancing to the following step.
  A playable workflow must declare at least one enabled completion step. The
  runtime still keeps the older viewer-only fallback where Next on an
  undeclared last step can complete and exit, but author new or repaired
  workflows with an explicit ending instead of relying on that compatibility
  path.
  It is NOT "the last step in the list". A branching workflow can finish
  anywhere, and the last entry in \`steps\` may be a detour nobody is meant to
  leave from — so this key says which step is the ending, and more than one step
  may carry it (each branch can have its own).
  Next is the only thing it affects: triggers still navigate without completing,
  Back still goes back, and the flag does nothing until Next is pressed. A step
  whose \`next_button\` carries no label has no Next button at all, so the flag
  can never fire there — validation warns about that rather than leaving it a
  silent no-op.
- \`completion_condition\` — optional \`{ type, delay_seconds }\`; when absent
  (the default) Next is available the moment the step arrives. When present it
  holds the Next BUTTON back until the condition is met — the learner sees it
  disabled, not hidden — and nothing else about the step changes: trigger
  buttons still branch and Back still goes back.
  - \`type: delay\` waits \`delay_seconds\` from the moment the step arrives. The
    delay is required on this kind and must be greater than 0; anything over
    ${MAX_COMPLETION_DELAY_SECONDS}s is clamped to it at playback.
  - \`type: script_finished\` waits for the step's own \`on_start\` script to
    settle — finished, stopped, or failed alike, because a script that errored
    has still stopped happening and a learner cannot be left holding a step
    over it. On a step with no on_start script there is nothing to wait for, so
    Next is available straight away and validation warns.
  - \`type: video_finished\` waits for the step's own \`media\` video to reach
    its end — the \`end\` timecode when the media is trimmed, otherwise the end
    of the file. Terminal either way, so a video that could not be loaded or
    could not be decoded also opens it rather than stranding the learner. The
    learner may pause and replay freely; the gate opens once and stays open —
    so on a \`loop: true\` clip it opens after the FIRST full playback and the
    learner is not held for a second lap. On a step whose media is an image, has
    no url, or is absent there is nothing to wait for, so Next is available
    straight away and validation warns.
  - It applies on the learner's FIRST arrival at the step only. Coming back to
    a step they have already visited is never gated again — the condition
    guards what they see before moving on the first time, and charging the wait
    again on the way back would toll every pass, worst on a step a trigger
    loops through. So a delay is waited out once per run, not once per visit.
    (Arrival is what counts, not completion: a step they landed on and left
    before the wait elapsed is still covered ground.)
  Use it where moving on early would skip something the learner is meant to
  watch or hear — \`video_finished\` on a step whose whole point is the clip,
  \`script_finished\` on a demonstration, \`delay\` where there is no event to
  wait on. Do not reach for it as a pace-setter on ordinary steps: a disabled
  button with no explanation reads as a broken one, so say in the step's
  instructions what they are waiting for.
  Presence REPLACES the whole block and \`null\` clears it, like \`attachment\`
  and \`media\`.
- \`user_input_allowed\` — per-step override; absent inherits the header's list,
  which itself defaults to nothing. Same members, \`all\` included. An EMPTY list
  is not the same as absent — it is an explicit "nothing here", which is how a
  mostly-open workflow closes one step down, and a step's list REPLACES the
  header's rather than adding to it.
- \`attachment\` — \`{ part, point }\`; while the step plays a dashed 3D line runs
  from the play panel to it. \`part\` is a slash-joined DISPLAY-NAME path
  (\`Robodog/Fan\`) because it is rendered to the author — NOT the \`0,1,2\` index
  path the scene tools take. \`point\` is optional; without it the part's origin
  is used. \`attachment: null\` clears it.
- \`step_view\` — see the block below.
- \`media\` — see the block below.
- \`triggers\` — the step's branch buttons. Pressing one navigates WITHOUT
  completing the current step.
- \`scripts\` — the prepared, executable scripts. Yours.

**\`steps[].completion_condition\`** — accepted keys:
${list(WORKFLOW_COMPLETION_CONDITION_KEYS)}. \`type\` is a strict enum
(${list(COMPLETION_CONDITIONS)}) and an unrecognised word is an error naming it.

**\`steps[].step_view\`** — accepted keys: ${list(WORKFLOW_STEP_VIEW_KEYS)}.
Optional; the camera framing the step opens on. When the step is activated the
camera FLIES to it, at the active scene's own transition speed — the same move a
scene change makes to a scene's saved view. A step without one leaves the camera
wherever it is, which is the default and the right choice for most steps.

- \`position\` — where the camera sits. \`{ x, y, z }\`, world-space metres.
- \`pivot\` — the world point the camera looks at and orbits. Same shape.
- **Both are required for the step to move the camera.** The pair IS the whole
  view — the camera is an orbit rig, so the direction and the zoom both fall out
  of it (the zoom is the distance between them) and there is no rotation to
  store. A block with only one half is a warning and moves nothing, rather than
  an error, so a half-written capture costs that step its framing and nothing
  else.
- The numbers come from \`get_camera_view\`, which reads the live viewport. They
  are not values to reason about, estimate or adjust: nothing about a scene's
  contents tells you where a camera should sit, and a pose invented from a part's
  coordinates points somewhere arbitrary. Ask for the shot to be framed, then
  capture it.
- It moves the camera and persists NOTHING: the scene's own saved view is
  untouched, so the client's Reset still returns to the scene's framing rather
  than to the step's.
- On the patch path, presence **REPLACES the whole block** and \`step_view: null\`
  clears it — the same rule \`attachment\` and \`media\` follow. A patch naming only
  \`position\` does not inherit the previous \`pivot\`: it clears it, leaving the
  half-written block above, because a camera at a new position aimed at an old
  pivot looks at the wrong thing.

**\`steps[].media\`** — accepted keys: ${list(WORKFLOW_MEDIA_KEYS)}. Optional; an
image or a video shown while the step plays, in a SECOND panel beside the play
panel (in a headset, a second slate angled toward the wearer).

- \`url\` — the whole reference, and the only part that is required. An
  \`https://…\` address is loaded as-is. A file uploaded through the Asset
  Library's **Media** tab is addressed by the \`campfire://media/<file>\`
  reference that tab hands out — do NOT invent one of those: the file has to
  exist in the library for it to resolve, and there is no tool to upload one.
  It is obtained from that tab, which copies it.
- \`type\` — strict enum ${list(MEDIA_TYPES)}. OPTIONAL: when absent the kind is
  read from the url's extension, so \`{ url: …/clip.mp4 }\` is already a video.
  Set it explicitly when the url has no telling extension.
- \`fit\` — strict enum ${list(MEDIA_FITS)}. OPTIONAL, and absent means
  \`fill\`. It decides whether the media may be CROPPED to fill the frame it is
  shown in:
  - \`fill\` leaves the framing to the surface. Playback shows the media in the
    stage that surface's design chose, and one whose stage is not the media's
    own shape may crop it to fill: today that is a video in the full-screen
    presentation a media-only step gets on a landscape phone, which crops
    equally top and bottom so the video fills the screen.
  - \`contain\` guarantees the opposite: all of the media is always visible, in
    every surface, letterboxed where the stage's shape differs. Set it where
    losing the edges would lose CONTENT — a diagram, a screenshot, a UI capture,
    a video with text near its edges — rather than as a general default.
  It does not change the stage's SHAPE: a \`contain\` media in the panel beside a
  step is still shown in that panel's 16:9 stage, letterboxed rather than
  reshaping the panel.
- \`name\` — the media panel's header label. Empty falls back to the step's name.
- \`description\` — prose shown under the media. Distinct from the step's own
  \`step_context\` and \`step_instructions\`: this one describes the MEDIA.
- \`transcript\` — **video only**: what is said in the clip, as text. Free
  prose, and multi-line: paragraphs, one per speaker turn or topic, are the
  normal shape.
  - It is what makes a video FINDABLE. The guide reads the workflow with
    \`get_workflow\`, so a learner who quotes a phrase they remember hearing —
    rather than naming the step — is answered by matching their words against
    the transcripts and being taken to that step. Nothing else in the file can
    answer that: a step's name and its \`step_context\` describe the STEP rather
    than what its clip says. Write one whenever a clip says something a learner
    might come back for; leave it out for a wordless one.
  - It is not narration for the guide to read out, and not a substitute for
    \`step_instructions\`. The learner can open it beside the video.
  - The authored view renders it as a labelled BLOCK (\`Media transcript:\` with
    the prose below it), not as one line — see §5's note on the reserved
    constructs, which are the same two \`step_instructions\` reserves.
- \`start\` / \`end\` — **video only**: where playback starts and stops, as
  seconds (\`90\`), \`mm:ss\` (\`1:30\`), or \`hh:mm:ss\`. Validation errors on text
  that is not a timecode and on an \`end\` that is not after its \`start\`.
- \`loop\` — **video only**, a boolean. OPTIONAL, and absent means play once.
  \`true\` plays the clip continuously: each time it reaches its end — the
  \`end\` timecode when it is trimmed, otherwise the end of the file — it
  restarts from its start and keeps going, until the learner pauses it or
  leaves the step.
  - It does NOT hold the learner: a \`completion_condition\` of
    \`video_finished\` on the same step opens after the FIRST full playback, so
    Next becomes available one lap in while the clip carries on. A looping clip
    and that condition are the normal pairing for "watch this at least once,
    then move on when you are ready".
  - Reach for it on a short clip the learner refers back to while they work — a
    few seconds of a mechanism turning, a gesture repeated — and leave it alone
    for anything with a beginning and an end, where a restart reads as the
    player having failed to stop.
- \`autoplay\` — **video only**, a boolean. OPTIONAL, and absent or \`false\`
  means the video waits for the learner or an authored \`play_video\` command.
  \`true\` starts it as soon as the step arrives. On a scene step that opens the
  video full size and it collapses again when non-looping playback finishes; a
  media-only step is already full size. \`autoplay: true\` and \`play_video\` are
  alternatives: autoplay is arrival-time behavior, while the command chooses a
  particular point in the step's script.
- The five video-only keys on an \`image\` are a warning, not an error — but they
  do nothing, so they usually mean the \`type\` is wrong.
- On the patch path, presence **REPLACES the whole block** and \`media: null\`
  clears it — the same rule \`attachment\` follows, and for the same reason: it is
  one thing rather than a bag of independent fields. A patch that named only
  \`url\` and inherited the previous \`transcript\` would leave a transcript
  describing a different video, so send every key the block should end up with.

**\`steps[].triggers[]\`** — accepted keys: ${list(WORKFLOW_TRIGGER_KEYS.filter((key) => key !== "raw"))}.
\`type\` is a strict enum (${list(STEP_TRIGGER_TYPES)}). \`label\` is what the learner reads. \`go_to\` must
name a real step or a non-empty group.

**\`steps[].scripts[]\`** — accepted keys: ${list(WORKFLOW_SCRIPT_KEYS)}.

- \`id\` — required and unique within the step; \`play_workflow_script\` selects on it.
- \`trigger\` — strict enum ${list(SCRIPT_TRIGGERS)}. \`on_start\` plays when the
  step becomes current — after the step's \`scene\` has been activated, so the
  script starts in the right scene and needs no \`go_to_scene\` step of its own;
  \`manual\` plays only when something asks for it. There
  is no on-completion trigger: a step's script runs when the step becomes
  current, and that is the only automatic playback.
- \`revert\` — strict enum ${list(SCRIPT_REVERTS)}, default \`none\`. What the
  script's scene changes do afterwards: \`none\` keeps them, \`on_action_finish\`
  puts the scene back when the script ends (including when the learner
  navigates away mid-script), \`on_scene_change\` keeps them visible until the
  learner leaves the scene the script ran in. Transforms, visibility, names,
  materials, spin, explosion, isolation, camera and selection revert;
  structural edits (creating, deleting, renaming or reordering scenes and
  models) do not.
- \`steps[]\` — accepted keys: ${list(WORKFLOW_SCRIPT_STEP_KEYS)}. Each entry is
  either \`{ say: <line> }\` or \`{ tool: <tool_id>, args: { ... } }\` — never both.
  \`args\` is allowed only on a tool step. It is a REAL YAML mapping, not a JSON
  string, and its keys must be parameters of that tool — the same arguments the
  live tool call takes. A bare string entry is read as a \`say\`.
  A \`say\` may be the exact token \`${SAY_STEP_INSTRUCTIONS_TOKEN}\` instead of a
  line of its own: it speaks the OWNING step's \`step_instructions\` at playback,
  so the panel's copy and the spoken line cannot drift apart. Prefer it over
  pasting the instructions into a \`say\` — that is the same words in two places,
  and it goes stale the moment the author edits one of them. It resolves when
  the script PLAYS, not when it is written, so later edits to the instructions
  are picked up. A step with no \`step_instructions\` makes the token speak
  nothing: the line is skipped and validation warns about it.
  Every \`say\` — the token and a literal line alike — is spoken with any Markdown
  markers taken off, so write the line as it should sound and use markup only for
  how it should look on the panel.
  The commands available and their arguments are listed in the
  **"Script command reference"** at the end of this document. An \`args\` key
  that is not a parameter of the named command refuses the whole document or
  patch; a tool id that does not exist, an argument of the wrong type, a value
  outside a declared enum, or a missing required one is reported against the
  step instead.

An unrecognised \`trigger\` / \`revert\` / trigger \`type\` / top-level \`type\`
is NOT silently defaulted: the stored parse and the patch both
**refuse** the document, naming the value and the allowed enum. The same
refusal applies to an unknown key, a mixed say/tool step, and \`args\` keys
that are not parameters of a registered tool.

## Runtime text tokens

Learner-facing text may contain \`${USER_NAME_TOKEN}\`, which stands for the
signed-in learner's first name and is replaced with it at playback. The file
always holds the token; the substitution happens on the way to the screen or the
spoken line, so one document greets every learner by their own name. \`<user_name>\`
is accepted as the same token, and the match ignores case.

It is a substitution inside a string, so it can sit anywhere in a sentence and a
string may contain several. It resolves in the text a learner reads or hears:
\`step_instructions\`, \`next_button\` and \`back_button\` labels, a trigger's
\`label\`, \`media\`'s \`name\` / \`description\` / \`transcript\`, and a script's
\`say\` line — including the line a \`${SAY_STEP_INSTRUCTIONS_TOKEN}\` say resolves
to, since those instructions are authored text like any other.

It does NOT resolve in a NAME — a step's, a group's or the workflow's — nor in
any id, \`go_to\` target, \`scene\` reference, part path, url or tool argument.
Those are addresses: a step is found by its name, so a displayed name that
differed from the written one would be a step nobody could then ask for. A name
containing the token shows it literally.

With no first name available the token resolves to nothing rather than to a
stand-in word, and the surrounding text still reads.

## Presence: absent vs empty vs null

Authored string fields have three distinct states, and they mean different
things on the patch path:

| Key as written | Result |
| --- | --- |
| the key, with a value | set to that value |
| the key, with \`""\` | present-but-empty — the editor renders the bare label for the author |
| the key, with \`null\` | cleared — the field becomes absent and its label stops rendering |
| no key at all | left exactly as it was |

## Patch semantics (\`update_workflow\`'s \`yaml\`)

A patch is SPARSE and uses this same schema. Accepted top-level keys:
${list(WORKFLOW_PATCH_KEYS)}.
Accepted header keys: ${list(WORKFLOW_PATCH_HEADER_KEYS)}.
Accepted step keys: ${list(WORKFLOW_PATCH_STEP_KEYS)}.

| Case | Behaviour |
| --- | --- |
| Key present | applied |
| Key absent | **left untouched** |
| Key explicitly \`null\` | cleared |
| Step matched | by \`id\` first, then by \`name\` |
| Step not found | **appended as a NEW step** — inside its \`group\` when given, else at the end (joining whatever group is open there) |
| \`id\` sent with a different \`name\` | renames the step and rewrites trigger \`go_to\` targets naming the old name |
| \`scripts\` present | **REPLACES** that step's whole script list — include every script it should end up with. \`scripts: []\` empties it; \`scripts: null\` is an error |
| \`triggers\` present | **REPLACES** the whole list; \`triggers: null\` clears it |
| \`media\` present | **REPLACES** the whole block — include every key it should end up with; \`media: null\` clears it |
| \`attachment\` present | same rule as \`media\`; \`attachment: null\` clears it |
| \`step_view\` present | same rule as \`media\`; an omitted \`position\` or \`pivot\` is CLEARED, not inherited. \`step_view: null\` clears it |
| \`group: <name>\` | moves the step to the END of that group's run (creating the group when new) |
| \`group: null\` | moves the step to the end of the ungrouped head run |
| No file exists yet | created from the patch, then stamped with \`uid\` / \`type\` / \`version\` / \`project\` |
| Stored file is unreadable | **full-file repair mode** — \`yaml\` must be the entire corrected raw file from \`get_workflow\`; it replaces the broken bytes after a full-document parse, with identity re-stamped by the app |
| \`${WORKFLOW_RUNTIME_KEY}\` present in a sparse patch | **refused** |
| \`${WORKFLOW_UI_KEY}\` present in a sparse patch | **refused** |
| ${list(WORKFLOW_IDENTITY_KEYS)} present in a sparse patch | **refused** |
| Unknown key, bad enum, inapplicable param, malformed YAML | **refused**, naming the key or the line |

A refusal rejects the WHOLE patch — nothing is half-applied. The error names
what was wrong; fix that rather than retrying the same patch.

${buildScriptCommandReference()}
`;
}
